import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPen,
  faTrash,
  faUserPlus,
  faArrowRightArrowLeft,
  faPlay,
  faPaperPlane,
  faCheck,
  faRotateLeft,
  faHandshake,
  faXmark,
  faNoteSticky,
  faImage,
  faClipboardCheck,
  faCircle,
  faClockRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { auditLogService } from '@/services/audit-log.service';
import { SkeletonList } from '@/components/skeletons';

interface Props {
  inspectionId: string;
}

// Labels/styles cover the events AuditLogWorker.HANDLED_EVENTS already records
// for an Inspection (backend/src/inspection/events/inspection.events.ts +
// backend/src/procedure/events/procedure.events.ts). Anything not listed here
// falls back to its raw event name — this is honestly a partial activity feed,
// not a complete one: finding notes, finding-level authorization, and
// ownership-transfer events are known, pre-existing audit gaps (see
// SERVICE_JOB_LIFECYCLE_SPECIFICATION_v1.0.md §14), not something this
// component invents coverage for.
const EVENT_LABELS: Record<string, string> = {
  'inspection.created': 'Inspection Created',
  'inspection.updated': 'Inspection Updated',
  'inspection.deleted': 'Inspection Deleted',
  'inspection.assigned': 'Engineer Assigned',
  'inspection.status-changed': 'Status Changed',
  'inspection.submitted': 'Submitted for OPS Review',
  'inspection.started': 'Inspection Started',
  'inspection.claimed': 'Claimed from Job Pool',
  'inspection.unclaimed': 'Unclaimed',
  'inspection.transferred': 'Ownership Transferred',
  'inspection.operations.approved': 'Approved for Commercial',
  'inspection.operations.returned': 'Returned to Engineer',
  'inspection.commercial.sent': 'Sent to Customer',
  'inspection.commercial.returned': 'Returned to Operations',
  'inspection.customer.approved': 'Customer Approved',
  'inspection.customer.rejected': 'Customer Rejected',
  'inspection.customer.modification-requested': 'Customer Requested Modification',
  // D-131 — job-level modification rounds and quotation revisions (M5 labels).
  'inspection.modification.round-opened': 'Modification Request Opened',
  'inspection.modification.round-triaged': 'Modification Request Classified',
  'inspection.modification.ops-verdict-recorded': 'Operations Feasibility Response',
  'inspection.modification.clarification-requested': 'Question Sent to Customer',
  'inspection.modification.customer-replied': 'Customer Replied',
  'inspection.modification.round-closed': 'Modification Request Closed',
  'inspection.quotation.revision-sent': 'Revised Quotation Sent',
  'inspection.quotation.revision-decided': 'Customer Decided on Revised Quotation',
  'inspection.quotation.revision-superseded': 'Quotation Revision Superseded',
  'inspection.finding.created': 'Finding Added',
  'inspection.finding.updated': 'Finding Updated',
  'inspection.finding.deleted': 'Finding Deleted',
  'inspection.attachment.uploaded': 'Attachment Uploaded',
  'inspection.attachment.deleted': 'Attachment Deleted',
  'inspection.note.created': 'Internal Note Added',
  'procedure.execution.initialized': 'Procedure Assigned',
  'procedure.step.status_changed': 'Procedure Step Updated',
  'procedure.step.evidence_uploaded': 'Procedure Evidence Uploaded',
};

const EVENT_STYLES: Record<string, { icon: IconDefinition; dot: string }> = {
  'inspection.created': { icon: faPlus, dot: 'bg-primary/15 text-primary' },
  'inspection.updated': { icon: faPen, dot: 'bg-accent/15 text-accent' },
  'inspection.deleted': { icon: faTrash, dot: 'bg-destructive/15 text-destructive' },
  'inspection.assigned': { icon: faUserPlus, dot: 'bg-blue-500/15 text-blue-700' },
  'inspection.status-changed': { icon: faCircle, dot: 'bg-muted text-muted-foreground' },
  'inspection.submitted': { icon: faPaperPlane, dot: 'bg-teal-500/15 text-teal-700' },
  'inspection.started': { icon: faPlay, dot: 'bg-amber-500/15 text-amber-700' },
  'inspection.claimed': { icon: faUserPlus, dot: 'bg-blue-500/15 text-blue-700' },
  'inspection.unclaimed': { icon: faXmark, dot: 'bg-muted text-muted-foreground' },
  'inspection.transferred': {
    icon: faArrowRightArrowLeft,
    dot: 'bg-orange-500/15 text-orange-700',
  },
  'inspection.operations.approved': { icon: faCheck, dot: 'bg-green-500/15 text-green-700' },
  'inspection.operations.returned': { icon: faRotateLeft, dot: 'bg-red-500/15 text-red-700' },
  'inspection.commercial.sent': { icon: faHandshake, dot: 'bg-blue-500/15 text-blue-700' },
  'inspection.commercial.returned': { icon: faRotateLeft, dot: 'bg-red-500/15 text-red-700' },
  'inspection.customer.approved': { icon: faCheck, dot: 'bg-green-500/15 text-green-700' },
  'inspection.customer.rejected': { icon: faXmark, dot: 'bg-red-500/15 text-red-700' },
  'inspection.customer.modification-requested': {
    icon: faPen,
    dot: 'bg-amber-500/15 text-amber-700',
  },
  // D-131 — same tones as the legacy customer-decision events they extend.
  'inspection.modification.round-opened': { icon: faPen, dot: 'bg-amber-500/15 text-amber-700' },
  'inspection.modification.round-triaged': {
    icon: faClipboardCheck,
    dot: 'bg-purple-500/15 text-purple-700',
  },
  'inspection.modification.ops-verdict-recorded': {
    icon: faClipboardCheck,
    dot: 'bg-teal-500/15 text-teal-700',
  },
  'inspection.modification.clarification-requested': {
    icon: faPaperPlane,
    dot: 'bg-indigo-500/15 text-indigo-700',
  },
  'inspection.modification.customer-replied': {
    icon: faPen,
    dot: 'bg-indigo-500/15 text-indigo-700',
  },
  'inspection.modification.round-closed': { icon: faCheck, dot: 'bg-green-500/15 text-green-700' },
  'inspection.quotation.revision-sent': { icon: faHandshake, dot: 'bg-blue-500/15 text-blue-700' },
  'inspection.quotation.revision-decided': {
    icon: faCheck,
    dot: 'bg-green-500/15 text-green-700',
  },
  'inspection.quotation.revision-superseded': {
    icon: faRotateLeft,
    dot: 'bg-muted text-muted-foreground',
  },
  'inspection.finding.created': { icon: faPlus, dot: 'bg-primary/15 text-primary' },
  'inspection.finding.updated': { icon: faPen, dot: 'bg-accent/15 text-accent' },
  'inspection.finding.deleted': { icon: faTrash, dot: 'bg-destructive/15 text-destructive' },
  'inspection.attachment.uploaded': { icon: faImage, dot: 'bg-muted text-muted-foreground' },
  'inspection.attachment.deleted': { icon: faTrash, dot: 'bg-destructive/15 text-destructive' },
  'inspection.note.created': { icon: faNoteSticky, dot: 'bg-primary/10 text-primary' },
  'procedure.execution.initialized': { icon: faClipboardCheck, dot: 'bg-accent/15 text-accent' },
  'procedure.step.status_changed': {
    icon: faClipboardCheck,
    dot: 'bg-muted text-muted-foreground',
  },
  'procedure.step.evidence_uploaded': { icon: faImage, dot: 'bg-muted text-muted-foreground' },
};

function getStyle(eventName: string): { icon: IconDefinition; dot: string } {
  return EVENT_STYLES[eventName] ?? { icon: faCircle, dot: 'bg-muted text-muted-foreground' };
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Mirrors EquipmentTimeline.tsx's shape for the inspection entity (kept as a
 * separate component rather than generalizing the equipment one, so the
 * already-shipped Equipment feature is not touched by this change). */
export function InspectionTimeline({ inspectionId }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['inspection-timeline', inspectionId],
    queryFn: () => auditLogService.getInspectionTimeline(inspectionId),
    staleTime: 30_000,
  });

  if (isLoading) return <SkeletonList rows={4} />;

  if (!data || data.items.length === 0) {
    return (
      <div className="py-8 text-center">
        <FontAwesomeIcon
          icon={faClockRotateLeft}
          className="mb-2 h-8 w-8 text-muted-foreground/30"
        />
        <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {data.items.map((entry, i) => {
        const style = getStyle(entry.eventName);
        const label = EVENT_LABELS[entry.eventName] ?? entry.eventName;
        return (
          <div key={entry.id} className="relative flex gap-3 pb-4">
            {i < data.items.length - 1 && (
              <div className="absolute left-4 top-8 bottom-0 w-px bg-border" />
            )}
            <div
              className={`relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${style.dot} ring-2 ring-card`}
            >
              <FontAwesomeIcon icon={style.icon} className="h-3.5 w-3.5" />
            </div>
            <div className="flex-1 pt-1 min-w-0">
              <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                <span className="text-sm font-medium text-foreground">{label}</span>
                <span className="flex-shrink-0 text-xs text-muted-foreground">
                  {formatDate(entry.occurredAt)}
                </span>
              </div>
              {entry.actorName && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  by {entry.actorName}
                  {entry.actorRoleName && (
                    <span className="text-muted-foreground/70"> ({entry.actorRoleName})</span>
                  )}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
